// Seesaw: pivots around its centre (fixed in the level). The body is always drawn, also in the build phase.
import { definePart } from '../core/registry';
import { block, lg, shadowOn, shadowOff, C, DEG } from '../core/draw-helpers';
import type { Ctx2D, GameBody, Vec } from '../core/types';
import { drawStone } from './stone';

export function drawSeesaw(ctx: Ctx2D, b: { pivot?: Vec; position: Vec; angle: number }) {
  const { x, y } = b.pivot || b.position;
  // foot
  ctx.save(); shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.moveTo(x, y + 2); ctx.lineTo(x - 24, y + 40); ctx.lineTo(x + 24, y + 40); ctx.closePath();
  ctx.fillStyle = lg(ctx, y, y + 40, C.lilac1, C.lilac2); ctx.fill(); shadowOff(ctx); ctx.restore();
  block(ctx, b.position.x, b.position.y, 230, 14, b.angle, '#FFB86B', C.wood2, 7);
  ctx.beginPath(); ctx.arc(x, y, 7, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = C.ink; ctx.stroke();
}

export const seesaw = definePart({
  rects: [{ x: 0, y: 0, w: 230, h: 14 }], rot: 'tilt', thumb: false,
  // the body is drawn instead of the placement
  placement: 'never',
  create(api, pl, { fixed, idx }) {
    const a = Math.max(-20, Math.min(20, pl.a || 0)) * DEG;
    const b = api.add(api.R(pl.x, pl.y, 230, 14, { density: 0.002, friction: 0.4, restitution: 0.05, angle: a, label: 'seesaw' }));
    b.kind = 'seesaw'; b.partIdx = idx; b.fixedPart = fixed; b.pivot = { x: pl.x, y: pl.y }; b.padImmune = true;
    const c = api.Constraint.create({ pointA: { x: pl.x, y: pl.y }, bodyB: b, pointB: { x: 0, y: 0 }, stiffness: 1, length: 0 });
    api.Composite.add(api.world, c);
    api.S.dyn.push(b);
  },
  draw: (ctx, pl) => drawSeesaw(ctx, { pivot: { x: pl.x, y: pl.y }, position: { x: pl.x, y: pl.y }, angle: (pl.a as number) * DEG }),
  icon: ctx => drawSeesaw(ctx, { pivot: { x: 0, y: 0 }, position: { x: 0, y: 0 }, angle: 0.3 }),
  // the seesaw must not be covered by other parts
  blockers: f => { const out = []; for (let i = -2; i <= 2; i++) out.push({ x: f.x + i * 50, y: f.y + i * 50 * Math.tan((f.a || 0) * DEG), r: 22 }); return out; },
  art: {
    seesaw: {
      layer: 'under', thumb: 3,
      draw: (ctx, b) => drawSeesaw(ctx, b),
      reskin: (b: GameBody) => ({ t: 'seesaw', x: b.pivot!.x, y: b.pivot!.y, a: b.angle / DEG, body: b }),
    },
  },
  system: {
    // limit the swing
    afterUpdate(api) {
      const { Body } = api;
      for (const b of api.S.dyn) if (b.kind === 'seesaw') {
        const lim = 24 * DEG;
        if (b.angle > lim) { Body.setAngle(b, lim); if (b.angularVelocity > 0) Body.setAngularVelocity(b, 0); }
        if (b.angle < -lim) { Body.setAngle(b, -lim); if (b.angularVelocity < 0) Body.setAngularVelocity(b, 0); }
      }
    },
  },
  intro(x, t, g, { fakeBall, arrow, ease }) {
    const flip = ease((t - 60) / 10);
    const ang = (20 - 44 * flip) * DEG;
    drawSeesaw(x, { pivot: { x: 230, y: 200 }, position: { x: 230, y: 200 }, angle: ang });
    const sy = t < 60 ? 20 + 0.05 * t * t : 200 - 108 * Math.sin(-ang) - 30;
    drawStone(x, 125, Math.min(sy, 200 + 108 * Math.sin(-ang) - 28 - 4), 0);
    let by = 200 + 108 * Math.sin(ang) - 24;
    if (t > 62 && t < 170) { const k = (t - 62) / 108; by = by - 180 * Math.sin(k * Math.PI); }
    g.ball(x, fakeBall(335, by, 0, t > 62 && t < 116 ? -8 : 2), 'hero', t);
    arrow(70, 90, 70, 60); arrow(400, 110, 400, 70, true);
  },
});
